/** Normaliza CNPJ para comparação (somente dígitos). */
export function normalizeClientCnpjKey(cnpj: unknown): string {
  return String(cnpj ?? '').replace(/\D/g, '');
}

/** Identificador de cliente esperado a partir do contrato clicado na lista. */
export function resolveExpectedClientIdentity(contractInfo: {
  clientId?: string | null;
  clientCnpj?: string | null;
  client?: { id?: string; cnpj?: string } | null;
}): { clientId: string | null; clientCnpj: string | null } {
  const clientId =
    contractInfo.clientId ?? contractInfo.client?.id ?? null;
  const clientCnpj =
    contractInfo.clientCnpj ?? contractInfo.client?.cnpj ?? null;
  return { clientId: clientId ?? null, clientCnpj: clientCnpj ?? null };
}

export type LinesModalClientValidationResult =
  | { ok: true }
  | { ok: false; message: string };

/**
 * Garante que todos os contratos retornados pelo modal de grupo pertencem ao mesmo
 * cliente do contrato aberto (clientId e/ou CNPJ).
 */
export function validateGroupModalClientConsistency(
  groupContracts: Array<{
    id?: string;
    clientId?: string | null;
    clientCnpj?: string | null;
    client?: { id?: string; cnpj?: string } | null;
  }>,
  expected: { clientId: string | null; clientCnpj: string | null }
): LinesModalClientValidationResult {
  if (!groupContracts?.length) {
    return { ok: true };
  }

  const expectedId = expected.clientId ?? null;
  const expectedCnpjKey = normalizeClientCnpjKey(expected.clientCnpj);
  const hasExpectedId = Boolean(expectedId);
  const hasExpectedCnpj = expectedCnpjKey.length === 14;

  if (!hasExpectedId && !hasExpectedCnpj) {
    return {
      ok: false,
      message:
        'Não foi possível validar o cliente do contrato selecionado. O script CQV não será gerado — recarregue a lista e tente novamente.',
    };
  }

  for (const contract of groupContracts) {
    const rowId = contract.clientId ?? contract.client?.id ?? null;
    const rowCnpjKey = normalizeClientCnpjKey(
      contract.clientCnpj ?? contract.client?.cnpj
    );

    if (hasExpectedId && rowId && rowId !== expectedId) {
      return {
        ok: false,
        message:
          'Os contratos do grupo retornados pela API pertencem a clientes diferentes. Por segurança, o script CQV não será exibido. Recarregue os detalhes ou contate o suporte.',
      };
    }

    if (
      hasExpectedCnpj &&
      rowCnpjKey.length === 14 &&
      rowCnpjKey !== expectedCnpjKey
    ) {
      return {
        ok: false,
        message:
          'Os contratos do grupo retornam CNPJs diferentes do contrato selecionado. O script CQV foi bloqueado até os dados serem corrigidos na base.',
      };
    }
  }

  return { ok: true };
}

/** Contrato âncora no grupo: o clicado, ou o primeiro membro da resposta da API. */
export function pickGroupModalAnchorContract<T extends { id?: string }>(
  groupResponse: T[],
  clickedContractId: string
): T {
  return (
    groupResponse.find((c) => c.id === clickedContractId) ?? groupResponse[0]
  );
}

export const LINES_MODAL_CQV_BLOCKED_PLACEHOLDER =
  'Script CQV indisponível: os dados do cliente no modal não passaram na validação de consistência. Corrija ou recarregue os detalhes antes de ligar.';

export const EMPTY_WHATSAPP_CQV_PARTS = {
  part1: '',
  part2: '',
  part3: '',
} as const;

/** Indica se o useEffect do script CQV deve chamar os geradores. */
export function shouldGenerateCqvScripts(input: {
  blockedReason: string | null;
  hasContractInfo: boolean;
  lineCount: number;
}): boolean {
  return (
    !input.blockedReason &&
    input.hasContractInfo &&
    input.lineCount > 0
  );
}
